import { useResource } from './useResource.ts'

export interface BufferObject {
  /** Bind the buffer object to the target for which it was created, then run `body`, then unbind the target. */
  bind(body: () => void): void

  /** Fill the buffer with the given data, unless it is the data with which the buffer was last filled. */
  fill(data: AllowSharedBufferSource): void
}

/**
 * Create a GL buffer object.
 * @param name The name of the buffer, for debugging.
 * @param gl The GL.
 * @param target The target to which the buffer will be bound.
 * @param usage 
 * @returns 
 */
export function useBufferObject(
  name: string,
  gl: WebGL2RenderingContext,
  target: GLenum,
  usage: GLenum = gl.STATIC_DRAW
): BufferObject {
  return useResource(function* () {
    const bo = gl.createBuffer()
    if (!bo) {
      throw new Error('gl.createBuffer() failed')
    }
    gl.getExtension('GMAN_debug_helper')?.tagObject(bo, name)

    let priorData: AllowSharedBufferSource | null = null

    const bufferObject: BufferObject = {
      bind(body: () => void): void {
        gl.bindBuffer(target, bo)
        body()
        gl.bindBuffer(target, null)
      },

      fill(data: AllowSharedBufferSource): void {
        if (data === priorData) {
          return
        }

        priorData = data

        gl.bindBuffer(target, bo)
        gl.bufferData(target, data, usage)
        gl.bindBuffer(target, null)
      },
    }

    yield bufferObject

    gl.deleteBuffer(bo)
  }, [gl, name, target, usage])
}
